import { useCallback, useRef } from 'react';
import { useTraceMap } from '../../tracer/hooks/useTraceMap';
import { TRACER_STATUS_CODE } from '../../tracer/utils/tracerStatusCode';
import { useWebSocket } from '../../web-socket-provider/hooks/useWebSocket';
import { CHAT_AGENT_MESSAGE_TYPE } from '../config/chatAgentMessageType';
import { useChatAgentTracer } from './useChatAgentTracer';

export const useChatAgentWebSocketTracer = () => {
    const { getTraceName, removeTrace } = useTraceMap();
    const { endTrace, startChildSpan, endChildSpan, addTraceAttributes } = useChatAgentTracer();
    const traceIdsRef = useRef(new Set());

    const traceWsMessage = useCallback(
        message => {
            const traceId = message?.traceId;
            if (!traceId) {
                return;
            }

            const rootName = getTraceName(traceId);
            if (!rootName) {
                return;
            }

            const contextId = message?.contextId;
            const componentName = message?.componentData?.componentName;
            const componentValue = message?.componentData?.value;
            const isFirstMessage = !traceIdsRef.current.has(traceId);

            if (
                componentValue === CHAT_AGENT_MESSAGE_TYPE.MESSAGE_END ||
                componentValue === CHAT_AGENT_MESSAGE_TYPE.MESSAGE_ERROR
            ) {
                addTraceAttributes(rootName, {
                    ...(contextId && { contextId }),
                });
                endTrace(
                    rootName,
                    componentValue === CHAT_AGENT_MESSAGE_TYPE.MESSAGE_ERROR
                        ? TRACER_STATUS_CODE.ERROR
                        : TRACER_STATUS_CODE.OK
                );
                traceIdsRef.current.delete(traceId);
                removeTrace(traceId);
                return;
            }

            if (isFirstMessage) {
                traceIdsRef.current.add(traceId);
            }

            const childName = startChildSpan(`WS_${componentName || CHAT_AGENT_MESSAGE_TYPE.MESSAGE}`, rootName, {
                componentName,
                contextId,
                isFirstMessage,
            });
            endChildSpan(childName);
        },
        [addTraceAttributes, endChildSpan, endTrace, getTraceName, removeTrace, startChildSpan]
    );

    useWebSocket(traceWsMessage);
};
